import { useMemo } from "react";
import { LinkButton } from "../../common/components/Link";
import { useIsMinDesktopSize } from "../../common/hooks/useMediaQuery";
import type ComponentTypes from "../componentTypes";
import classNames from "classnames";
import { resolveUrl } from "../../utils/url/urlResolver";
import { trackBannerClick } from "../../feature/Analytics/ga/contentStream";
import { Icon } from "../../common/components/icon/Icon";
import { IconType } from "../../common/components/icon/IconType";
import { cleanupText } from "./CMSSection";

export interface CMSBannerProps {
  type: ComponentTypes.CMSBanner;
  headlineTag?: string;
  headline?: string;
  subheadline?: string;
  linkUrl?: string;
  linkLabel?: string;
  imageUrl?: string;
  mobileImageUrl?: string;
  className?: string;
  target?: "newWindow" | "sameWindow";
}

export const CMSBanner = ({
  headline,
  headlineTag,
  subheadline,
  imageUrl,
  mobileImageUrl,
  linkLabel,
  linkUrl,
  className,
  target,
}: CMSBannerProps) => {
  const isDesktopMode = useIsMinDesktopSize();

  headline = cleanupText(headline);
  subheadline = cleanupText(subheadline);
  linkLabel = cleanupText(linkLabel);

  const showContentBox = !!(headline || subheadline || linkLabel);
  const Headline = (headlineTag?.trim() ?? "p") as keyof JSX.IntrinsicElements;

  const bgImageStyle = useMemo(() => {
    const bgImageUrl = pickImage([mobileImageUrl, imageUrl], isDesktopMode);
    if (bgImageUrl) {
      return {
        backgroundImage: `url(${bgImageUrl})`,
      };
    }
    return undefined;
  }, [imageUrl, mobileImageUrl, isDesktopMode]);

  const content = (
    <>
      {bgImageStyle && (
        <div className="scha__banner__background-container">
          <div className={"scha__banner__background"} style={bgImageStyle} />
        </div>
      )}
      {showContentBox && (
        <div className={"scha__banner__content"}>
          {headline && <Headline className={"scha__banner__headline"}>{headline}</Headline>}
          {subheadline && <p className={"scha__banner__headline scha__banner__subheadline"}>{subheadline}</p>}
          {linkLabel && (
            <LinkButton
              iconBefore={<Icon className={"scha__link--arrow-link"} type={IconType.ChevronRight} size={20} />}
              className={"scha__banner__link"}
            >
              {linkLabel}
            </LinkButton>
          )}
        </div>
      )}
    </>
  );

  const cls = classNames("scha__banner", className);

  if (linkUrl) {
    return (
      <a
        href={resolveUrl(linkUrl)}
        className={cls}
        target={target === "newWindow" ? "_blank" : undefined}
        onClick={() => trackBannerClick(linkUrl)}
      >
        {content}
      </a>
    );
  } else {
    return (
      <div className={cls} style={bgImageStyle}>
        {content}
      </div>
    );
  }
};

const pickImage = (images: (string | undefined)[], isDesktop: boolean) => {
  let image: string | undefined;
  const index = isDesktop ? 1 : 0;
  image = images[index];
  if (!image) {
    image = images.filter((url) => !!url)[0];
  }
  return image;
};
